import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useQuery } from "@tanstack/react-query";
import { LogOut } from "lucide-react";
import { NavLink, Route, Routes } from "react-router-dom";

import { getMe } from "@/lib/api";
import { currentEmail, DEV_BYPASS_AUTH } from "@/lib/auth";
import { AutorizacionesPage } from "@/pages/AutorizacionesPage";
import { ConfiguracionPage } from "@/pages/ConfiguracionPage";
import { EquipoAnioPage } from "@/pages/EquipoAnioPage";
import { EquipoMesPage } from "@/pages/EquipoMesPage";
import { LoginPage } from "@/pages/LoginPage";
import { MisVacacionesPage } from "@/pages/MisVacacionesPage";
import { ResponsivasPage } from "@/pages/ResponsivasPage";

export function App() {
  const isAuthenticated = useIsAuthenticated();
  const { instance } = useMsal();
  // Registra al empleado al entrar y nos dice si es jefe (para la pestaña).
  const meQ = useQuery({
    queryKey: ["me"],
    queryFn: getMe,
    staleTime: Infinity,
    retry: false,
    enabled: DEV_BYPASS_AUTH || isAuthenticated,
  });

  if (!DEV_BYPASS_AUTH && !isAuthenticated) {
    return <LoginPage />;
  }

  const email = currentEmail();
  const esJefe = meQ.data?.es_jefe ?? false;

  const handleLogout = () => {
    if (DEV_BYPASS_AUTH) return;
    instance.logoutPopup({ postLogoutRedirectUri: window.location.origin });
  };

  const navCls = ({ isActive }: { isActive: boolean }) =>
    `px-3 py-1.5 rounded text-sm ${
      isActive ? "bg-slate-100 text-slate-900 font-medium" : "text-slate-600 hover:bg-slate-50"
    }`;

  return (
    <div className="min-h-full flex flex-col">
      <header className="border-b bg-white px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <img src="/logo-mark.png" alt="MPP Energía" className="w-7 h-7" />
            <span className="font-semibold text-lg">Organización MPP</span>
            <span className="text-xs uppercase tracking-wide text-slate-400">Vacaciones</span>
          </div>
          <nav className="flex items-center gap-1">
            <NavLink to="/" end className={navCls}>
              Mis vacaciones
            </NavLink>
            <NavLink to="/mes" className={navCls}>
              Equipo por mes
            </NavLink>
            <NavLink to="/anio" className={navCls}>
              Equipo por año
            </NavLink>
            {esJefe && (
              <NavLink to="/autorizaciones" className={navCls}>
                Autorizaciones
              </NavLink>
            )}
            <NavLink to="/responsivas" className={navCls}>
              Responsivas
            </NavLink>
            <NavLink to="/configuracion" className={navCls}>
              Configuración
            </NavLink>
          </nav>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-sm text-slate-600">{email}</div>
          <button
            type="button"
            onClick={handleLogout}
            className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 text-sm"
            title="Cerrar sesión"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>
      <main className="flex-1 px-6 py-6">
        <Routes>
          <Route path="/" element={<MisVacacionesPage />} />
          <Route path="/mes" element={<EquipoMesPage />} />
          <Route path="/anio" element={<EquipoAnioPage />} />
          <Route path="/autorizaciones" element={<AutorizacionesPage />} />
          <Route path="/responsivas" element={<ResponsivasPage />} />
          <Route path="/configuracion" element={<ConfiguracionPage />} />
        </Routes>
      </main>
    </div>
  );
}
